/**
 * US-E16.F1-1023 (@UC0464 · security S4 MEDIUM-1) — bỏ SIÊU DỮ LIỆU khỏi ảnh trước khi lưu.
 *
 * Ảnh hóa đơn hiện cho mọi người xem được đợt thu (QĐ 22/10). Ảnh chụp bằng điện thoại thường mang EXIF: toạ độ GPS nơi chụp, model máy,
 * giờ chụp — tức có thể lộ địa chỉ nhà người chụp cho cả nhóm. App không lọc được đáng tin (`image_picker` không mã hoá lại, bản Android
 * còn CHÉP thẻ GPS khi thu nhỏ) ⇒ lọc ở server, trên chính byte sẽ lưu.
 *
 * Chỉ BỎ đoạn/chunk, KHÔNG giải mã/mã hoá lại ảnh: dữ liệu ảnh giữ nguyên byte, không mất chất lượng.
 *
 * 🔴 DANH SÁCH CHO PHÉP, không phải danh sách cấm (review bảo mật vòng 2): GPS còn nằm được ở APP3 (Kodak META), APP6 (GoPro GPMF), APP11
 * (JUMBF/C2PA), chunk `caBX` của PNG, chunk `C2PA` của WEBP… — chặn từng loại thì loại thứ N+1 lọt. Giữ đúng thứ cần để VẼ ảnh, bỏ hết phần
 * còn lại:
 *  - JPEG: đoạn vẽ ảnh (SOFn · DHT · DAC · DQT · DNL · DRI · DHP · EXP · SOS + dữ liệu quét); APP0 «JFIF/JFXX» · APP2 «ICC_PROFILE» (màu)
 *    · APP14 «Adobe» (cần để đọc đúng màu). EXIF bỏ, CHỈ giữ lại thẻ Orientation (APP1 tối giản — thiếu nó ảnh chụp ngang hiện xoay 90°).
 *    Bỏ MỌI byte sau EOI (ảnh phụ MPF của iPhone/Samsung, gain map, motion photo).
 *  - PNG: chunk ảnh/màu/hiển thị (`PNG_KEEP_CHUNKS`); dừng ở IEND.
 *  - WEBP: chunk ảnh/màu (`WEBP_KEEP_CHUNKS`); hạ cờ EXIF/XMP ở VP8X; tính lại kích thước RIFF. Ảnh động ⇒ từ chối.
 *
 * 🔴 Chống cạn bộ nhớ (review bảo mật vòng 2 HIGH): chép vào MỘT bộ đệm cấp sẵn cỡ đầu vào (không tạo một object mỗi đoạn — tệp 10 MB toàn
 * đoạn 4 byte từng làm heap tăng ~677 MB) + trần số đoạn [IMAGE_SEGMENTS_MAX] (tệp thật chỉ vài chục đoạn / vài nghìn chunk IDAT).
 *
 * Cấu trúc hỏng / vượt trần ⇒ `null`: nơi gọi phải TỪ CHỐI, không được rơi về lưu bản chưa lọc (fail-closed).
 */
export function stripImageMetadata(buf: Buffer, contentType: string): Buffer | null {
  switch (contentType) {
    case "image/jpeg":
      return stripJpeg(buf);
    case "image/png":
      return stripPng(buf);
    case "image/webp":
      return stripWebp(buf);
    default:
      return null;
  }
}

/** Trần số đoạn (JPEG) / chunk (PNG · WEBP) mỗi tệp. */
export const IMAGE_SEGMENTS_MAX = 65_536;

/** Bộ đệm ghi một lần: cấp sẵn cỡ đầu vào (đầu ra không bao giờ dài hơn), chép theo khoảng. */
class Output {
  private readonly out: Buffer;
  private w = 0;
  constructor(capacity: number) {
    this.out = Buffer.alloc(capacity);
  }
  copy(src: Buffer, start: number, end: number): void {
    src.copy(this.out, this.w, start, end);
    this.w += end - start;
  }
  write(bytes: Buffer): void {
    this.copy(bytes, 0, bytes.length);
  }
  /** Ghi đè một byte ĐÃ chép (vị trí tính từ đầu ra). */
  patch(at: number, value: number): void {
    this.out[at] = value;
  }
  get length(): number {
    return this.w;
  }
  done(): Buffer {
    return this.out.subarray(0, this.w);
  }
}

const JPEG_SOS = 0xda;
const JPEG_EOI = 0xd9;
/**
 * Byte latin1 của một chuỗi ASCII — `Uint8Array`, KHÔNG `Buffer`: hằng cấp module chạy lúc NẠP, mà gói này nạp cả ở trình duyệt (admin qua
 * `@goppy/contracts`), nơi không có `Buffer` ⇒ trắng trang (US-E16.F1-1027). `Buffer.equals` / `Buffer.concat` nhận `Uint8Array` y như `Buffer`.
 */
const latin1 = (s: string): Uint8Array => Uint8Array.from(s, (c) => c.charCodeAt(0));

/** APPn được giữ, theo ĐỊNH DANH ở đầu nội dung đoạn. */
const JPEG_KEEP_APP: ReadonlyMap<number, readonly Uint8Array[]> = new Map([
  [0xe0, [latin1("JFIF\0"), latin1("JFXX\0")]],
  [0xe2, [latin1("ICC_PROFILE\0")]],
  [0xee, [latin1("Adobe")]],
]);

const isAppMarker = (marker: number) => marker >= 0xe0 && marker <= 0xef;

/** Đoạn KHÔNG phải APPn cần để vẽ ảnh: SOF0..15 (trừ C8 dự trữ) · DHT C4 · DAC CC · DQT DB · DNL DC · DRI DD · DHP DE · EXP DF. Khác ⇒ bỏ. */
const isImageMarker = (marker: number) => (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc8) || (marker >= 0xdb && marker <= 0xdf);

function keepJpegSegment(buf: Buffer, marker: number, payloadStart: number, end: number): boolean {
  // COM (0xFE) không phải APPn và không thuộc đoạn vẽ ảnh ⇒ bỏ theo cùng danh sách cho phép.
  if (!isAppMarker(marker)) return isImageMarker(marker);
  const ids = JPEG_KEEP_APP.get(marker);
  if (!ids) return false;
  return ids.some((id) => end - payloadStart >= id.length && buf.subarray(payloadStart, payloadStart + id.length).equals(id));
}

const EXIF_ID = latin1("Exif\0\0");
const EXIF_ORIENTATION_TAG = 0x0112;
/** «Exif\0\0» + đầu TIFF 8 + số mục 2 + một mục 12 + con trỏ IFD kế 4 — cũng là cỡ của APP1 tối giản ⇒ đầu ra không dài hơn đầu vào. */
const MIN_EXIF_PAYLOAD = 6 + 8 + 2 + 12 + 4;

/**
 * Hướng ảnh (thẻ Orientation 0x0112 ở IFD0) của một APP1 EXIF; `null` khi không có / ngoài 2..8 (1 = thẳng, không cần giữ) / cấu trúc hỏng.
 * Hướng ảnh KHÔNG phải siêu dữ liệu riêng tư: thiếu nó, ảnh chụp ngang của điện thoại hiện xoay 90° cho cả nhóm (review bảo mật vòng 3).
 */
function exifOrientation(buf: Buffer, payloadStart: number, end: number): { readonly orientation: number; readonly le: boolean } | null {
  if (end - payloadStart < MIN_EXIF_PAYLOAD || !buf.subarray(payloadStart, payloadStart + 6).equals(EXIF_ID)) return null;
  const tiff = payloadStart + 6;
  const order = buf.toString("latin1", tiff, tiff + 2);
  if (order !== "II" && order !== "MM") return null;
  const le = order === "II";
  const u16 = (at: number) => (le ? buf.readUInt16LE(at) : buf.readUInt16BE(at));
  const u32 = (at: number) => (le ? buf.readUInt32LE(at) : buf.readUInt32BE(at));
  if (u16(tiff + 2) !== 42) return null;
  const ifd = tiff + u32(tiff + 4);
  if (ifd < tiff + 8 || ifd + 2 > end) return null;
  const count = u16(ifd);
  for (let k = 0; k < count; k++) {
    const entry = ifd + 2 + k * 12;
    if (entry + 12 > end) return null;
    if (u16(entry) !== EXIF_ORIENTATION_TAG) continue;
    // SHORT × 1, giá trị nằm ngay trong mục.
    if (u16(entry + 2) !== 3 || u32(entry + 4) !== 1) return null;
    const orientation = u16(entry + 8);
    return orientation >= 2 && orientation <= 8 ? { orientation, le } : null;
  }
  return null;
}

/** APP1 tối giản: «Exif\0\0» + TIFF cùng thứ tự byte, IFD0 đúng MỘT mục Orientation, không IFD kế. */
function orientationApp1(orientation: number, le: boolean): Buffer {
  const tiff = Buffer.alloc(26);
  tiff.write(le ? "II" : "MM", 0, "latin1");
  const w16 = (v: number, at: number) => (le ? tiff.writeUInt16LE(v, at) : tiff.writeUInt16BE(v, at));
  const w32 = (v: number, at: number) => (le ? tiff.writeUInt32LE(v, at) : tiff.writeUInt32BE(v, at));
  w16(42, 2);
  w32(8, 4);
  w16(1, 8);
  w16(EXIF_ORIENTATION_TAG, 10);
  w16(3, 12);
  w32(1, 14);
  w16(orientation, 18);
  // byte 20..21 đệm của giá trị · 22..25 con trỏ IFD kế = 0 (Buffer.alloc đã điền 0).
  const header = Buffer.from([0xff, 0xe1, 0, 0]);
  header.writeUInt16BE(2 + EXIF_ID.length + tiff.length, 2);
  return Buffer.concat([header, EXIF_ID, tiff]);
}

/**
 * JPEG: đi TỪNG đoạn tới EOI rồi DỪNG. Dữ liệu quét (sau mỗi SOS) chép nguyên khoảng; quét nhiều lượt (progressive) thì các đoạn giữa hai
 * lượt quét qua cùng bộ lọc. Marker RSTn/TEM đứng NGOÀI dữ liệu quét là sai chuẩn ⇒ `null`.
 */
function stripJpeg(buf: Buffer): Buffer | null {
  if (buf.length < 4 || buf[0] !== 0xff || buf[1] !== 0xd8) return null;
  const out = new Output(buf.length);
  out.copy(buf, 0, 2);
  let sawScan = false;
  let wroteOrientation = false;
  let segments = 0;
  let i = 2;
  while (i < buf.length) {
    if (++segments > IMAGE_SEGMENTS_MAX) return null;
    if (buf[i] !== 0xff) return null;
    // Byte đệm 0xFF lặp trước marker là hợp lệ (không chép — chỉ chép `FF <marker>` của chính đoạn).
    let m = i + 1;
    while (m < buf.length && buf[m] === 0xff) m++;
    if (m >= buf.length) return null;
    const marker = buf[m]!;
    if (marker === JPEG_EOI) {
      // EOI trước mọi lượt quét = không có ảnh.
      if (!sawScan) return null;
      out.copy(buf, m - 1, m + 1);
      return out.done();
    }
    // Marker không độ dài (SOI · RSTn · TEM) ngoài dữ liệu quét ⇒ sai chuẩn.
    if (marker === 0xd8 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) return null;
    if (m + 2 >= buf.length) return null;
    const len = buf.readUInt16BE(m + 1);
    const end = m + 1 + len;
    if (len < 2 || end > buf.length) return null;
    if (marker === JPEG_SOS) {
      const scanEnd = endOfEntropyData(buf, end);
      if (scanEnd === null) return null;
      out.copy(buf, m - 1, scanEnd);
      sawScan = true;
      i = scanEnd;
      continue;
    }
    if (keepJpegSegment(buf, marker, m + 3, end)) {
      out.copy(buf, m - 1, end);
    } else if (marker === 0xe1 && !wroteOrientation) {
      // APP1 EXIF bị bỏ — nhưng GIỮ hướng ảnh bằng một APP1 tối giản đặt ĐÚNG chỗ cũ (không dài hơn đoạn gốc).
      const o = exifOrientation(buf, m + 3, end);
      if (o) {
        out.write(orientationApp1(o.orientation, o.le));
        wroteOrientation = true;
      }
    }
    i = end;
  }
  return null;
}

/**
 * Vị trí marker đầu tiên KẾT THÚC dữ liệu quét bắt đầu ở `from` (0xFF đầu tiên của nó, kể cả byte đệm). Trong dữ liệu quét, `FF 00` là
 * byte nhồi và `FF D0..D7` là RSTn — đều là dữ liệu. Hết tệp mà chưa gặp marker ⇒ `null` (tệp bị cắt). Tuyến tính, không cấp phát.
 */
function endOfEntropyData(buf: Buffer, from: number): number | null {
  let k = from;
  while (k < buf.length) {
    if (buf[k] !== 0xff) {
      k++;
      continue;
    }
    let n = k + 1;
    while (n < buf.length && buf[n] === 0xff) n++;
    if (n >= buf.length) return null;
    const b = buf[n]!;
    if (b === 0x00 || (b >= 0xd0 && b <= 0xd7)) {
      k = n + 1;
      continue;
    }
    return k;
  }
  return null;
}

const PNG_SIGNATURE = Uint8Array.of(0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a);
/** Chunk cần để VẼ ảnh PNG/APNG đúng màu, đúng tỉ lệ. Mọi chunk khác (eXIf · tEXt · iTXt · zTXt · tIME · caBX · chunk riêng…) bỏ. */
const PNG_KEEP_CHUNKS = new Set([
  "IHDR",
  "PLTE",
  "IDAT",
  "IEND",
  "tRNS",
  "cHRM",
  "gAMA",
  "iCCP",
  "sBIT",
  "sRGB",
  "pHYs",
  "bKGD",
  "acTL",
  "fcTL",
  "fdAT",
]);

function stripPng(buf: Buffer): Buffer | null {
  if (buf.length < 8 || !buf.subarray(0, 8).equals(PNG_SIGNATURE)) return null;
  const out = new Output(buf.length);
  out.copy(buf, 0, 8);
  let chunks = 0;
  let i = 8;
  while (i + 8 <= buf.length) {
    if (++chunks > IMAGE_SEGMENTS_MAX) return null;
    const len = buf.readUInt32BE(i);
    const type = buf.toString("latin1", i + 4, i + 8);
    const end = i + 12 + len;
    if (end > buf.length) return null;
    if (PNG_KEEP_CHUNKS.has(type)) out.copy(buf, i, end);
    i = end;
    if (type === "IEND") return out.done();
  }
  return null;
}

/** Chunk cần để VẼ ảnh WEBP tĩnh (kênh alpha, màu). Mọi chunk khác (EXIF · XMP · C2PA · chunk lạ…) bỏ. */
const WEBP_KEEP_CHUNKS = new Set(["VP8 ", "VP8L", "VP8X", "ALPH", "ICCP"]);
/** Ảnh động: khung `ANMF` chứa chunk CON (có thể là EXIF) — không lọc lồng, từ chối hẳn (hóa đơn không phải ảnh động). */
const WEBP_ANIMATION_CHUNKS = new Set(["ANIM", "ANMF"]);
/** Cờ VP8X: bit 3 = có EXIF, bit 2 = có XMP. */
const VP8X_METADATA_FLAGS = 0x08 | 0x04;

function stripWebp(buf: Buffer): Buffer | null {
  if (buf.length < 12 || buf.toString("latin1", 0, 4) !== "RIFF" || buf.toString("latin1", 8, 12) !== "WEBP") return null;
  const riffEnd = 8 + buf.readUInt32LE(4);
  if (riffEnd > buf.length) return null;
  const out = new Output(riffEnd);
  // Phần đầu RIFF chép tạm; kích thước ghi lại ở cuối.
  out.copy(buf, 0, 12);
  let chunks = 0;
  let i = 12;
  while (i < riffEnd) {
    if (++chunks > IMAGE_SEGMENTS_MAX) return null;
    if (i + 8 > riffEnd) return null;
    const type = buf.toString("latin1", i, i + 4);
    const size = buf.readUInt32LE(i + 4);
    const end = i + 8 + size + (size % 2);
    if (end > riffEnd) return null;
    if (WEBP_ANIMATION_CHUNKS.has(type)) return null;
    if (WEBP_KEEP_CHUNKS.has(type)) {
      const at = out.length;
      out.copy(buf, i, end);
      if (type === "VP8X" && size > 0) out.patch(at + 8, buf[i + 8]! & ~VP8X_METADATA_FLAGS);
    }
    i = end;
  }
  const result = out.done();
  result.writeUInt32LE(result.length - 8, 4);
  return result;
}
